import {
  type AudioClip,
  audioClipEnd,
  type ClipLayout,
  clipAt,
  clipCutout,
  clipDuration,
  clipFadeAt,
  coverRect,
  musicGainAt,
  type OverlayClip,
  overlayClipEnd,
  overlaySourceTimeAt,
  placeClip,
  placeOverlay,
  type SpeechRegion,
  sourceTimeAt,
  stackItems,
  stackSlot,
  type TransitionFrame,
  type TransitionLayer,
  transitionAt,
  transitionFrame,
  transitionGains,
  type VideoClip,
  type VideoStack,
} from "@cva/shared";
import type { AssetInfo } from "../commands";
import { colorFilterUrl, drawVignette } from "./color-filters";
import { MatteCompositor } from "./matte-gl";

/**
 * Preview playback of all video tracks (docs/05-video-und-untertitel.md §3).
 * Every visible clip gets a <video> element from a small pool; the next clips
 * are loaded and positioned ahead of time, so cuts play without a stutter.
 * The picture is composed in a canvas – main track (with transitions), then
 * the upper tracks – because Chrome paints visible <video> elements that were
 * paused, hidden or re-used unreliably. The player is imperative (no React
 * renders per frame) and reports the time to the video store, which drives
 * overlays, captions and the playhead.
 *
 * The clock follows the main track clip that plays (its sound must not
 * stutter); all other elements are kept in sync with it.
 *
 * Clips in a device mockup are the exception: the device is DOM (the same
 * `DeviceParts` as design documents, with CSS 3D transforms), the player
 * paints the picture into a canvas on its screen. To keep the track order,
 * every device has its own canvas above it for the clips of higher tracks.
 *
 * Clips with a cut-out person (packages/shared/src/video/cutout.ts) play
 * their matte video: the background comes from its picture part, the person
 * from picture × matte (matte-gl.ts).
 *
 * Elements can lie between the tracks (video/tracks.ts): the clips of each
 * video slot of the stack (`videoStack`) go into their own canvas, which the
 * page renderer puts between the bands of elements.
 */

export interface PlayerData {
  layout: ClipLayout[];
  overlays: OverlayClip[];
  assets: Map<string, AssetInfo>;
  frame: { width: number; height: number };
  duration: number;
  /** Page colour around pictures that do not fill the frame. */
  background: string;
  /** Clips in a device, bottom track first (their DOM is in the video layer). */
  devices?: DeviceTarget[];
  /** The persons of cut-out main track clips (`mainPersonClips`). */
  persons?: OverlayClip[];
  /** Drawing order of the tracks and the bands of elements between them. */
  stack?: VideoStack;
  /** Where the persons of the lowest cut-out track are drawn (`personLift`). */
  lift?: { split: number; track: number } | null;
  /** Full-frame canvases of the video slots above the first (index `slot - 1`), between the bands. */
  slots?: (HTMLCanvasElement | null)[];
}

/** A clip in a device mockup and the elements it is drawn with. */
export interface DeviceTarget {
  clip: OverlayClip;
  /** The device (positioned on the clip's box); hidden while the clip is not visible. */
  wrapper: HTMLElement;
  /** Canvas on the device's screen, null while the screen faces away. */
  screen: HTMLCanvasElement | null;
  /** Size of the screen in document px (the canvas gets this shape). */
  screenSize: { width: number; height: number } | null;
  /** Full-frame canvas above the device for the clips of higher tracks. */
  layer: HTMLCanvasElement;
}

/** Longest side of the preview canvas (the editor shows the frame scaled down). */
const SCREEN_MAX = 1280;
/** Elements are loaded and positioned this long before their clip starts. */
const PRELOAD_MS = 1500;
/** More elements than this are never kept (decoders are expensive). */
const POOL_MAX = 8;
/** A clip that does not start playing is waited for at most this long. */
const HOLD_MAX_MS = 400;

export function previewUrl(asset: AssetInfo | undefined): string | null {
  if (!asset) return null;
  return asset.media?.proxyUrl ?? asset.url;
}

/** The matte video of a clip with a cut-out person, once it is ready. */
function matteOf(asset: AssetInfo | undefined, cutout: boolean) {
  const matte = asset?.matte;
  return cutout && matte?.status === "ready" && matte.url && matte.layout
    ? { url: matte.url, layout: matte.layout }
    : null;
}

type Rect = { x: number; y: number; width: number; height: number };

interface Decoded {
  source: CanvasImageSource;
  display: { width: number; height: number };
  /** Part of `source` that shows the picture (matte videos), in source pixels. */
  crop?: Rect;
  /** Matte videos: the element and where picture and matte lie (fractions). */
  packed?: { el: HTMLVideoElement; layout: NonNullable<ReturnType<typeof matteOf>>["layout"] };
}

/** A decoded picture; "missing" when it will not come (no file, load error), null while decoding. */
type Picture = Decoded | "missing" | null;

interface Pooled {
  el: HTMLVideoElement;
  clipId: string | null;
  lastUsed: number;
}

/** A clip that is visible (or about to be) and what it shows at time `t`. */
interface ActiveClip {
  id: string;
  kind: "video" | "image";
  assetId: string;
  speed: number;
  /** Source position in ms. */
  source: number;
  volume: number;
  muted: boolean;
  /** Plays the matte video (person cut out). */
  cutout: boolean;
}

const mainActive = (entry: ClipLayout, t: number, gain = 1): ActiveClip => ({
  id: entry.clip.id,
  kind: entry.clip.kind,
  assetId: entry.clip.assetId,
  cutout: Boolean(clipCutout(entry.clip)),
  speed: entry.clip.speed,
  source: sourceTimeAt(entry, t),
  volume:
    entry.clip.volume * gain * clipFadeAt(entry.clip, t - entry.start, entry.end - entry.start),
  muted: entry.clip.muted,
});

const overlayActive = (clip: OverlayClip, t: number): ActiveClip => ({
  id: clip.id,
  kind: clip.kind,
  assetId: clip.assetId,
  cutout: Boolean(clipCutout(clip)),
  speed: clip.speed,
  source: overlaySourceTimeAt(clip, t),
  volume: clip.volume * clipFadeAt(clip, t - clip.start, clipDuration(clip)),
  muted: clip.muted,
});

export class CompositionPlayer {
  private pool: Pooled[] = [];
  private data: PlayerData = {
    layout: [],
    overlays: [],
    assets: new Map(),
    frame: { width: 1, height: 1 },
    duration: 0,
    background: "#000000",
  };
  private playing = false;
  private raf = 0;
  private clock = { t: 0, wall: 0 };
  private holdSince: number | null = null;
  private time = 0;
  private images = new Map<string, HTMLImageElement>();
  private layers: [HTMLCanvasElement, HTMLCanvasElement];
  /** Since when a picture waits for its decoder (the last frame stays up to 250 ms). */
  private waitingSince: number | null = null;

  constructor(
    private readonly host: HTMLElement,
    private readonly screen: HTMLCanvasElement,
    private readonly onTime: (t: number) => void,
    private readonly onEnded: () => void,
  ) {
    this.layers = [document.createElement("canvas"), document.createElement("canvas")];
  }

  setData(data: PlayerData) {
    this.data = data;
    const scale = Math.min(1, SCREEN_MAX / Math.max(data.frame.width, data.frame.height));
    const width = Math.max(2, Math.round(data.frame.width * scale));
    const height = Math.max(2, Math.round(data.frame.height * scale));
    const stack = (data.devices ?? []).map((device) => device.layer);
    const slots = (data.slots ?? []).filter((canvas) => canvas !== null);
    for (const canvas of [this.screen, ...this.layers, ...stack, ...slots]) {
      if (canvas.width !== width) canvas.width = width;
      if (canvas.height !== height) canvas.height = height;
    }
    if (!this.playing) this.showAt(this.time);
  }

  /** Positions all media for time `t` (paused or after a seek). */
  showAt(t: number) {
    this.time = t;
    this.clock = { t, wall: performance.now() };
    this.holdSince = null;
    this.sync(Math.min(t, Math.max(0, this.data.duration - 1)), true);
    this.draw();
  }

  play(from: number) {
    this.playing = true;
    this.showAt(from >= this.data.duration - 5 ? 0 : from);
    cancelAnimationFrame(this.raf);
    const loop = () => {
      if (!this.playing) return;
      this.tick();
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  pause() {
    this.playing = false;
    cancelAnimationFrame(this.raf);
    for (const item of this.pool) item.el.pause();
    this.draw();
  }

  destroy() {
    this.pause();
    // The elements belong to this player (StrictMode creates a new one with its own).
    for (const item of this.pool) {
      item.el.removeAttribute("src");
      item.el.load();
      item.el.remove();
    }
    this.pool = [];
  }

  // -------------------------------------------------------------------------
  // Clock
  // -------------------------------------------------------------------------

  private tick() {
    const now = performance.now();
    let t = this.clock.t + (now - this.clock.wall);
    const entry = clipAt(this.data.layout, this.time);
    const lead = entry?.clip.kind === "video" ? this.elementOf(entry.clip.id) : null;
    if (entry && lead && !lead.paused && lead.readyState >= 2) {
      const source = lead.currentTime * 1000;
      const byElement = entry.start + (source - entry.clip.sourceIn) / entry.clip.speed;
      if (byElement < entry.start + 1 && this.time <= entry.start + 1) {
        // The clip has not started yet (seek or decoder pending): the clock waits a moment.
        this.holdSince ??= now;
        t = now - this.holdSince < HOLD_MAX_MS ? entry.start : t;
      } else {
        this.holdSince = null;
        t = byElement;
      }
      if (source >= entry.clip.sourceOut - 12 || lead.ended) t = Math.max(t, entry.end);
    }
    this.clock = { t, wall: now };
    if (t >= this.data.duration) return this.finish();
    this.time = t;
    this.sync(t, false);
    this.draw();
    this.onTime(t);
  }

  private finish() {
    this.time = this.data.duration;
    this.onTime(this.data.duration);
    this.pause();
    this.onEnded();
  }

  // -------------------------------------------------------------------------
  // Media elements
  // -------------------------------------------------------------------------

  /** Clips visible at `t` (bottom to top), plus the ones starting soon (to preload). */
  private clipsAt(t: number): { visible: ActiveClip[]; upcoming: ActiveClip[] } {
    const visible: ActiveClip[] = [];
    const upcoming: ActiveClip[] = [];
    const { layout, overlays } = this.data;
    const transition = transitionAt(layout, t);
    const entry = clipAt(layout, t);
    if (transition) {
      const gains = transitionGains(transition.progress);
      visible.push(mainActive(transition.from, t, gains.from));
      visible.push(mainActive(transition.to, t, gains.to));
    } else if (entry) visible.push(mainActive(entry, t));
    const next = entry ? layout[entry.index + 1] : layout[0];
    if (next && next.start - t < PRELOAD_MS && next.start > t) {
      upcoming.push(mainActive(next, next.start));
    }
    for (const clip of overlays) {
      if (t >= clip.start && t < overlayClipEnd(clip)) visible.push(overlayActive(clip, t));
      else if (clip.start > t && clip.start - t < PRELOAD_MS) {
        upcoming.push(overlayActive(clip, clip.start));
      }
    }
    return { visible, upcoming };
  }

  private elementOf(clipId: string): HTMLVideoElement | null {
    return this.pool.find((item) => item.clipId === clipId)?.el ?? null;
  }

  /** The element of a clip: its own, a free one of the pool or a new one. */
  private acquire(clip: ActiveClip, busy: Set<HTMLVideoElement>): HTMLVideoElement | null {
    const asset = this.data.assets.get(clip.assetId);
    const url = matteOf(asset, clip.cutout)?.url ?? previewUrl(asset);
    // Without media info (still loading) nothing is assigned, so it is loaded again later.
    if (!url) return null;
    let item = this.pool.find((entry) => entry.clipId === clip.id);
    if (!item) {
      const free = this.pool
        .filter((entry) => !busy.has(entry.el))
        .sort((a, b) => a.lastUsed - b.lastUsed);
      item =
        free.find((entry) => entry.el.getAttribute("src") === url) ??
        (this.pool.length < POOL_MAX ? this.create() : free[0]);
      if (!item) return null;
      item.clipId = clip.id;
    }
    item.lastUsed = performance.now();
    if (item.el.getAttribute("src") !== url) {
      item.el.src = url;
      item.el.load();
    }
    busy.add(item.el);
    return item.el;
  }

  private create(): Pooled {
    const el = document.createElement("video");
    el.playsInline = true;
    el.preload = "auto";
    Object.assign(el.style, {
      position: "absolute",
      left: "0",
      top: "0",
      width: "2px",
      height: "2px",
      opacity: "0",
      pointerEvents: "none",
    });
    const redraw = () => {
      if (!this.playing) this.draw();
    };
    el.addEventListener("seeked", redraw);
    el.addEventListener("loadeddata", redraw);
    this.host.appendChild(el);
    const item = { el, clipId: null, lastUsed: 0 };
    this.pool.push(item);
    return item;
  }

  /**
   * Brings every element to its clip's position: visible clips play (or show
   * the frame when paused), upcoming ones wait at their start, the rest stop.
   */
  private sync(t: number, seeking: boolean) {
    const { visible, upcoming } = this.clipsAt(t);
    const busy = new Set<HTMLVideoElement>();
    const lead = clipAt(this.data.layout, t)?.clip.id;
    for (const clip of visible) {
      if (clip.kind !== "video") continue;
      const el = this.acquire(clip, busy);
      if (!el) continue;
      el.muted = clip.muted || clip.volume <= 0;
      el.volume = Math.max(0, Math.min(1, clip.volume));
      const target = clip.source / 1000;
      if (!this.playing || seeking) {
        if (!this.playing) el.pause();
        if (Math.abs(el.currentTime - target) > 0.03) el.currentTime = target;
        el.playbackRate = clip.speed;
      }
      if (!this.playing) continue;
      if (el.paused) {
        if (Math.abs(el.currentTime - target) > 0.05) el.currentTime = target;
        el.playbackRate = clip.speed;
        void el.play().catch(() => undefined);
        continue;
      }
      if (clip.id === lead) {
        el.playbackRate = clip.speed;
        continue;
      }
      // Followers: small drift is evened out by the playback rate, large drift by a seek.
      const drift = el.currentTime - target;
      if (Math.abs(drift) > 0.5 && this.holdSince === null) el.currentTime = target;
      else if (Math.abs(drift) > 0.04)
        el.playbackRate = clip.speed * (1 - Math.max(-0.08, Math.min(0.08, drift)));
      else el.playbackRate = clip.speed;
    }
    for (const clip of upcoming) {
      if (clip.kind !== "video") continue;
      const el = this.acquire(clip, busy);
      if (!el) continue;
      if (!el.paused) el.pause();
      const target = clip.source / 1000;
      if (Math.abs(el.currentTime - target) > 0.05) el.currentTime = target;
    }
    for (const item of this.pool) if (!busy.has(item.el) && !item.el.paused) item.el.pause();
  }

  private image(url: string): HTMLImageElement {
    let image = this.images.get(url);
    if (!image) {
      image = new Image();
      image.decoding = "async";
      image.onload = () => {
        if (!this.playing) this.draw();
      };
      image.src = url;
      this.images.set(url, image);
    }
    return image;
  }

  /** Picture of a clip, or null while it is not decoded yet. */
  private sourceOf(clip: VideoClip | OverlayClip): Picture {
    const asset = this.data.assets.get(clip.assetId);
    if (!asset) return "missing";
    let source: CanvasImageSource;
    let size: { width: number; height: number };
    const matte = matteOf(asset, Boolean(clipCutout(clip)));
    if (matte && clip.kind === "video") {
      const el = this.elementOf(clip.id);
      if (!el) return null;
      if (el.error) return "missing";
      // Still the proxy (the matte just got ready): wait for the matte video.
      if (el.readyState < 2 || el.getAttribute("src") !== matte.url) return null;
      const { color } = matte.layout;
      return {
        source: el,
        display: { width: asset.width ?? el.videoWidth, height: asset.height ?? el.videoHeight },
        crop: {
          x: color.x * el.videoWidth,
          y: color.y * el.videoHeight,
          width: color.width * el.videoWidth,
          height: color.height * el.videoHeight,
        },
        packed: { el, layout: matte.layout },
      };
    }
    if (clip.kind === "image") {
      const image = this.image(asset.url);
      if (image.naturalWidth === 0) return image.complete ? "missing" : null;
      source = image;
      size = { width: image.naturalWidth, height: image.naturalHeight };
    } else {
      const el = this.elementOf(clip.id);
      if (!el) return previewUrl(asset) ? null : "missing";
      if (el.error) return "missing";
      if (el.readyState < 2) return null;
      source = el;
      size = { width: el.videoWidth || 16, height: el.videoHeight || 9 };
    }
    // Placement uses the asset's display size (the proxy may be smaller, same aspect).
    return {
      source,
      display: { width: asset.width ?? size.width, height: asset.height ?? size.height },
    };
  }

  // -------------------------------------------------------------------------
  // Drawing
  // -------------------------------------------------------------------------

  /**
   * Composes the current picture: main track (with transitions), then the
   * upper tracks and cut-out persons in `stackItems` order – each into the
   * canvas of its slot between the bands of elements.
   */
  private draw() {
    const ctx = this.screen.getContext("2d");
    if (!ctx) return;
    const t = Math.min(this.time, Math.max(0, this.data.duration - 1));
    const { layout, overlays } = this.data;
    const transition = transitionAt(layout, t);
    const entry = clipAt(layout, t);
    const visible = (clip: OverlayClip) => t >= clip.start && t < overlayClipEnd(clip);
    const tops = overlays.filter(visible).sort((a, b) => a.track - b.track);
    const persons = (this.data.persons ?? []).filter(visible);
    const mains = transition ? [transition.from, transition.to] : entry ? [entry] : [];
    const pictures = new Map<string, Picture>();
    for (const clip of [...mains.map((item) => item.clip), ...tops]) {
      pictures.set(clip.id, this.sourceOf(clip));
    }
    // Keep the last picture until the new ones are decoded (no flicker while seeking);
    // while playing, a picture that takes too long is left out.
    const waiting = [...pictures.values()].some((picture) => picture === null);
    if (waiting) {
      const now = performance.now();
      this.waitingSince ??= now;
      if (!this.playing || now - this.waitingSince < 250) return;
    } else this.waitingSince = null;
    const frame = { width: this.screen.width, height: this.screen.height };
    const devices = this.data.devices ?? [];
    for (const device of devices) {
      device.layer.getContext("2d")?.clearRect(0, 0, frame.width, frame.height);
    }
    const slotContexts = (this.data.slots ?? []).map((canvas) => canvas?.getContext("2d") ?? null);
    for (const slot of slotContexts) slot?.clearRect(0, 0, frame.width, frame.height);
    ctx.save();
    ctx.clearRect(0, 0, frame.width, frame.height);
    if (transition) {
      const layers = this.layers.map((canvas) => canvas.getContext("2d")) as [
        CanvasRenderingContext2D | null,
        CanvasRenderingContext2D | null,
      ];
      if (layers[0] && layers[1]) {
        this.drawMain(layers[0], transition.from, pictures.get(transition.from.clip.id), t);
        this.drawMain(layers[1], transition.to, pictures.get(transition.to.clip.id), t);
        const kind = transition.to.clip.transition?.kind ?? "crossfade";
        composeTransition(ctx, this.layers, transitionFrame(kind, transition.progress), frame);
      }
    } else if (entry) {
      this.drawMain(ctx, entry, pictures.get(entry.clip.id), t);
    }
    const stack = this.data.stack ?? { bands: [] };
    const slotOf = (key: number) => stackSlot(stack, key);
    const slotContext = (slot: number) => (slot === 0 ? ctx : (slotContexts[slot - 1] ?? ctx));
    const lift = this.data.lift ?? null;
    const deviceSlot = (clip: OverlayClip) =>
      slotOf(stackItems([clip], [], lift).at(0)?.key ?? clip.track * 4);
    const shown = new Set<string>();
    for (const { part, clip, key } of stackItems(tops, persons, lift)) {
      const picture = pictures.get(clip.id);
      const slot = slotOf(key);
      if (clip.device) {
        const device = devices.find((target) => target.clip.id === clip.id);
        if (device && this.drawDevice(device, clip, picture, t)) shown.add(clip.id);
        continue;
      }
      if (!picture || picture === "missing") continue;
      // Above the devices of lower tracks in the same slot: into the canvas over the last of them.
      const group = devices.filter(
        (device) => device.clip.track < clip.track && deviceSlot(device.clip) === slot,
      );
      const target =
        group.length === 0 ? slotContext(slot) : group[group.length - 1]?.layer.getContext("2d");
      if (!target) continue;
      if (part === "picture") {
        this.drawOverlay(target, clip, picture, t);
        continue;
      }
      const person = picture.packed
        ? this.matte.render(picture.packed.el, picture.packed.layout)
        : null;
      if (person) {
        this.drawOverlay(target, clip, { source: person, display: picture.display }, t, true);
      }
    }
    for (const device of devices) {
      if (!shown.has(device.clip.id)) device.wrapper.style.visibility = "hidden";
    }
    ctx.restore();
  }

  private readonly matte = new MatteCompositor();

  /**
   * A clip in a device: the device (DOM) shows with the clip's opacity, the
   * picture goes onto the canvas of its screen – black around it, like the
   * export. Returns whether the device is visible.
   */
  private drawDevice(
    device: DeviceTarget,
    clip: OverlayClip,
    picture: Picture | undefined,
    t: number,
  ) {
    const opacity = clip.opacity * clipFadeAt(clip, t - clip.start, clipDuration(clip));
    if (opacity <= 0) return false;
    device.wrapper.style.opacity = String(opacity);
    device.wrapper.style.visibility = "visible";
    const canvas = device.screen;
    const size = device.screenSize;
    if (!canvas || !size) return true;
    // The same pixel density as the frame canvas.
    const scale = this.screen.width / this.data.frame.width;
    const width = Math.max(2, Math.round(size.width * scale));
    const height = Math.max(2, Math.round(size.height * scale));
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return true;
    ctx.save();
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, width, height);
    if (picture && picture !== "missing") {
      const area = { x: 0, y: 0, width, height };
      const placed = placeOverlay(area, picture.display, clip.framing);
      ctx.filter = colorFilterUrl(clip.color) ?? "none";
      drawPicture(ctx, picture.source, { ...placed, flipX: clip.framing.flipX });
      ctx.filter = "none";
      drawVignette(ctx, area, clip.color?.vignette);
    }
    ctx.restore();
    return true;
  }

  /** A main track clip over the whole frame: page colour or blurred copy, the picture, its fade. */
  private drawMain(
    ctx: CanvasRenderingContext2D,
    entry: ClipLayout,
    picture: Picture | undefined,
    t: number,
  ) {
    const canvas = ctx.canvas;
    const frame = { width: canvas.width, height: canvas.height };
    ctx.save();
    ctx.clearRect(0, 0, frame.width, frame.height);
    const clip: VideoClip = entry.clip;
    if (!picture || picture === "missing") {
      ctx.fillStyle = this.data.background;
      ctx.fillRect(0, 0, frame.width, frame.height);
      ctx.restore();
      return;
    }
    const { source, display, crop } = picture;
    const grade = colorFilterUrl(clip.color);
    if (clip.background === "blur") {
      const cover = coverRect(frame, display);
      ctx.filter = `${grade ?? ""} blur(${Math.max(4, Math.round(frame.width * 0.04))}px)`.trim();
      const grow = 1.15;
      drawPicture(
        ctx,
        source,
        {
          x: cover.x - (cover.width * (grow - 1)) / 2,
          y: cover.y - (cover.height * (grow - 1)) / 2,
          width: cover.width * grow,
          height: cover.height * grow,
          flipX: false,
        },
        crop,
      );
      ctx.filter = "none";
    } else {
      ctx.fillStyle = this.data.background;
      ctx.fillRect(0, 0, frame.width, frame.height);
    }
    const scale = frame.width / this.data.frame.width;
    // Split screens and cards: the picture fills its box, the page colour shows around it.
    const area = clip.box
      ? {
          x: clip.box.x * scale,
          y: clip.box.y * scale,
          width: clip.box.width * scale,
          height: clip.box.height * scale,
        }
      : { x: 0, y: 0, width: frame.width, height: frame.height };
    const placed = clip.box
      ? placeOverlay(area, display, clip.framing)
      : scaleRect(placeClip(this.data.frame, display, clip.framing), scale);
    ctx.save();
    if (clip.box) {
      ctx.beginPath();
      ctx.roundRect(
        area.x,
        area.y,
        area.width,
        area.height,
        Math.min((clip.radius ?? 0) * scale, area.width / 2, area.height / 2),
      );
      ctx.clip();
    }
    // A cut-out clip without its background: only the page colour or blur shows here.
    const hidden = clipCutout(clip)?.background === "remove";
    ctx.filter = grade ?? "none";
    if (!hidden) drawPicture(ctx, source, { ...placed, flipX: clip.framing.flipX }, crop);
    ctx.filter = "none";
    if (!hidden) drawVignette(ctx, area, clip.color?.vignette);
    ctx.restore();
    const fade = clipFadeAt(clip, t - entry.start, entry.end - entry.start);
    if (fade < 1) {
      ctx.fillStyle = `rgba(0, 0, 0, ${1 - fade})`;
      ctx.fillRect(0, 0, frame.width, frame.height);
    }
    ctx.restore();
  }

  /** A clip of an upper track in its box: rounded corners, opacity, fades. */
  private drawOverlay(
    ctx: CanvasRenderingContext2D,
    clip: OverlayClip,
    picture: Pick<Decoded, "source" | "display" | "crop">,
    t: number,
    /** A cut-out person: like a still with transparency. */
    person = false,
  ) {
    const scale = ctx.canvas.width / this.data.frame.width;
    const box = {
      x: clip.box.x * scale,
      y: clip.box.y * scale,
      width: clip.box.width * scale,
      height: clip.box.height * scale,
    };
    const placed = placeOverlay(box, picture.display, clip.framing);
    const opacity = clip.opacity * clipFadeAt(clip, t - clip.start, clipDuration(clip));
    if (opacity <= 0) return;
    // A vignette darkens only the picture itself, like the export – for stills with
    // transparency (logos, stickers) it is drawn onto them in a scratch canvas first.
    const vignette = clip.color?.vignette ?? 0;
    const target =
      vignette > 0 && (clip.kind === "image" || person) ? this.scratchContext(ctx.canvas) : ctx;
    if (!target) return;
    target.save();
    if (target === ctx) ctx.globalAlpha = opacity;
    else target.clearRect(0, 0, target.canvas.width, target.canvas.height);
    target.beginPath();
    target.roundRect(
      box.x,
      box.y,
      box.width,
      box.height,
      Math.min(clip.radius * scale, box.width / 2, box.height / 2),
    );
    target.clip();
    target.filter = colorFilterUrl(clip.color) ?? "none";
    drawPicture(target, picture.source, { ...placed, flipX: clip.framing.flipX }, picture.crop);
    target.filter = "none";
    if (vignette > 0) {
      // Only where the picture is, the gradient over the box.
      target.beginPath();
      target.rect(placed.x, placed.y, placed.width, placed.height);
      target.clip();
      if (target !== ctx) target.globalCompositeOperation = "source-atop";
      drawVignette(target, box, vignette);
    }
    target.restore();
    if (target !== ctx) {
      ctx.save();
      ctx.globalAlpha = opacity;
      ctx.drawImage(target.canvas, 0, 0);
      ctx.restore();
    }
  }

  private scratch: HTMLCanvasElement | null = null;

  /** An empty canvas of the screen's size for pictures composed on their own. */
  private scratchContext(like: HTMLCanvasElement): CanvasRenderingContext2D | null {
    this.scratch ??= document.createElement("canvas");
    if (this.scratch.width !== like.width) this.scratch.width = like.width;
    if (this.scratch.height !== like.height) this.scratch.height = like.height;
    return this.scratch.getContext("2d");
  }
}

function scaleRect(rect: { x: number; y: number; width: number; height: number }, k: number) {
  return { x: rect.x * k, y: rect.y * k, width: rect.width * k, height: rect.height * k };
}

/** Draws `source` (or the `crop` part of it) into `rect`. */
function drawPicture(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  rect: { x: number; y: number; width: number; height: number; flipX: boolean },
  crop?: Rect,
) {
  const draw = (x: number, y: number) =>
    crop
      ? ctx.drawImage(
          source,
          crop.x,
          crop.y,
          crop.width,
          crop.height,
          x,
          y,
          rect.width,
          rect.height,
        )
      : ctx.drawImage(source, x, y, rect.width, rect.height);
  if (rect.flipX) {
    ctx.save();
    ctx.translate(rect.x + rect.width, rect.y);
    ctx.scale(-1, 1);
    draw(0, 0);
    ctx.restore();
  } else {
    draw(rect.x, rect.y);
  }
}

/** Draws a full-frame layer with the offset, scale and visible part of a transition. */
function drawLayer(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  layer: TransitionLayer,
  frame: { width: number; height: number },
) {
  ctx.save();
  if (layer.clip) {
    ctx.beginPath();
    ctx.rect(
      layer.clip.x * frame.width,
      layer.clip.y * frame.height,
      layer.clip.width * frame.width,
      layer.clip.height * frame.height,
    );
    ctx.clip();
  }
  const width = frame.width * layer.scale;
  const height = frame.height * layer.scale;
  ctx.drawImage(
    canvas,
    (frame.width - width) / 2 + layer.dx * frame.width,
    (frame.height - height) / 2 + layer.dy * frame.height,
    width,
    height,
  );
  ctx.restore();
}

const smoothstep = (x: number) => {
  const t = Math.max(0, Math.min(1, x));
  return t * t * (3 - 2 * t);
};

/** Puts the two pictures of a transition together like ffmpeg's xfade does. */
function composeTransition(
  ctx: CanvasRenderingContext2D,
  [from, to]: [HTMLCanvasElement, HTMLCanvasElement],
  state: TransitionFrame,
  frame: { width: number; height: number },
) {
  if (state.mode === "mix") {
    // Weights add up to 1: summed on black, like a linear mix.
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, frame.width, frame.height);
    ctx.globalCompositeOperation = "lighter";
    if (state.white > 0) {
      ctx.globalAlpha = state.white;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, frame.width, frame.height);
    }
    ctx.globalAlpha = state.from.opacity;
    drawLayer(ctx, from, state.from, frame);
    ctx.globalAlpha = state.to.opacity;
    drawLayer(ctx, to, state.to, frame);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";
    return;
  }
  drawLayer(ctx, from, state.from, frame);
  if (state.mode === "stack") {
    drawLayer(ctx, to, state.to, frame);
    return;
  }
  // radial: the incoming picture inside a soft circle growing from the centre.
  const toCtx = to.getContext("2d");
  if (!toCtx) return;
  const radius = Math.hypot(frame.width / 2, frame.height / 2);
  const outer = state.outer ?? 1;
  const inner = state.inner ?? 0;
  if (outer <= 0) return;
  if (inner < 1.5) {
    const gradient = toCtx.createRadialGradient(
      frame.width / 2,
      frame.height / 2,
      0,
      frame.width / 2,
      frame.height / 2,
      outer * radius,
    );
    for (let step = 0; step <= 8; step++) {
      const distance = (step / 8) * outer;
      gradient.addColorStop(step / 8, `rgba(0, 0, 0, ${1 - smoothstep(distance - inner)})`);
    }
    toCtx.save();
    toCtx.globalCompositeOperation = "destination-in";
    toCtx.fillStyle = gradient;
    toCtx.fillRect(0, 0, frame.width, frame.height);
    toCtx.restore();
  }
  ctx.drawImage(to, 0, 0, frame.width, frame.height);
}

/** Music and voice-over clips: one <audio> element each, kept in sync with the timeline. */
export class MusicPlayer {
  private elements = new Map<string, HTMLAudioElement>();
  private clips: AudioClip[] = [];
  private assets = new Map<string, AssetInfo>();
  private speech: SpeechRegion[] = [];
  private duration = 0;

  setData(
    clips: AudioClip[],
    assets: Map<string, AssetInfo>,
    speech: SpeechRegion[],
    duration: number,
  ) {
    this.clips = clips;
    this.assets = assets;
    this.speech = speech;
    this.duration = duration;
    const ids = new Set(clips.map((clip) => clip.id));
    for (const [id, el] of this.elements) {
      if (!ids.has(id)) {
        el.pause();
        el.removeAttribute("src");
        this.elements.delete(id);
      }
    }
  }

  sync(t: number, playing: boolean) {
    for (const clip of this.clips) {
      const url = this.assets.get(clip.assetId)?.url;
      if (!url) continue;
      let el = this.elements.get(clip.id);
      if (!el) {
        el = new Audio();
        el.preload = "auto";
        this.elements.set(clip.id, el);
      }
      if (el.getAttribute("src") !== url) el.src = url;
      const inside = t >= clip.start && t < Math.min(audioClipEnd(clip), this.duration);
      const target = (clip.sourceIn + t - clip.start) / 1000;
      if (!inside || !playing) {
        el.pause();
        if (inside && Math.abs(el.currentTime - target) > 0.05) el.currentTime = target;
        continue;
      }
      if (Math.abs(el.currentTime - target) > 0.25) el.currentTime = target;
      el.volume = Math.max(0, Math.min(1, musicGainAt(clip, t, this.duration, this.speech)));
      if (el.paused) void el.play().catch(() => undefined);
    }
  }

  destroy() {
    for (const el of this.elements.values()) {
      el.pause();
      el.removeAttribute("src");
    }
    this.elements.clear();
  }
}
